import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { getProductId } from '@/infrastructure/product-info';
import { getTimeWindow } from '@/infrastructure/utils/timestamp';
import { toArray } from '@/infrastructure/utils/to-array';
import { FieldsOperator } from '@/presentation/components/form/proto';
import { FIELD_ALL, FieldKey, StandardCommonFieldKey, Stats } from '@/proto/field';
import { IFilter, IGetDashboardDataReq, IGetDashboardDataRsp, MetricSubType, MetricType, TimeType } from '@/proto/metric';
import { getFilterKeyForCustomTag, KeyDataType } from '@/proto/token';
import { fromPBTime, NoUndefinedField, toPBTime } from '@raft/utils';
import { toLineString } from '@raft/utils/src/case/snake-case';

export interface Dashboard {
  data: {
    metricName: string,
    dataPoints: (string | number)[]
  }[];
  timePoints: string[];
  timeType?: TimeType;
}

/**
 * 拉取 dashboard 接口
 * @param metricType
 * @param metricSubTypes
 * @param fields
 * @param stats
 */
export const loadDashboardNew = async (
  metricType: MetricType,
  metricSubTypes: MetricSubType[],
  timeWindow: any,
  filters: IFilter[],
  fields?: Record<string, any>,
  stats?: Stats,
): Promise<Dashboard> => {
  try {
    const { time_range: timeRange } = fields || {};
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes: metricSubTypes,
      optionalFilters: filters,
      startTime: toPBTime(moment(timeRange?.[0])),
      endTime: toPBTime(moment(timeRange?.[1])),
      timeWindow,
      extendOption: {
        stats,
      },
    };
    const rsp = await TRPCPost<NoUndefinedField<IGetDashboardDataRsp>>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );

    return {
      data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
        metricName: item.metricName,
        dataPoints: item.dataPoints,
      })),
      timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
      timeType: timeWindow,
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      data: [],
      timePoints: [],
      timeType: timeWindow,
    };
  }
};

/**
 * 改新表单UI后的filter结构，获取数据
 * @param metricType
 * @param metricSubTypes
 * @param fields
 * @param stats
 * @returns
 */
export const loadDashboardByStandardFilters = async (
  metricType: MetricType,
  metricSubTypes: MetricSubType[],
  fields: Record<string, any>,
  filters: IFilter[],
  stats?: Stats,
): Promise<Dashboard> => {
  const timeRange = fields?.[StandardCommonFieldKey.TimeRange]?.date_value;
  const timeType = fields?.time_window_type;

  try {
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes: metricSubTypes,
      optionalFilters: filters.filter(item => item.name !== 'report_time'),
      startTime: toPBTime(moment(timeRange?.[0])),
      endTime: toPBTime(moment(timeRange?.[1])),
      timeWindow: timeType,
      extendOption: {
        stats,
      },
    };
    const rsp = await TRPCPost<NoUndefinedField<IGetDashboardDataRsp>>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );

    return {
      data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
        metricName: item.metricName,
        dataPoints: item.dataPoints,
      })),
      timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
      timeType,
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      data: [],
      timePoints: [],
      timeType,
    };
  }
};

/**
 * 拉取 dashboard 接口
 * @param metricType
 * @param metricSubTypes
 * @param fields
 * @param stats
 */
export const loadDashboard = async (
  metricType: MetricType,
  metricSubTypes: MetricSubType[],
  fields: Record<string, any>,
  stats?: Stats,
): Promise<Dashboard> => {
  const timeRange = fields[FieldKey.TimeRange];
  const timeType = fields[FieldKey.TimeWindow] || getTimeWindow(timeRange?.[0], timeRange?.[1], metricType);
  const optionalFilter = filterOption(fields);
  try {
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes: metricSubTypes,
      optionalFilters: optionalFilter,
      startTime: toPBTime(moment(timeRange?.[0]).startOf('day')),
      endTime: toPBTime(moment(timeRange?.[1]).endOf('day')),
      timeWindow: timeType,
      extendOption: {
        stats,
      },
    };
    const rsp = await TRPCPost<NoUndefinedField<IGetDashboardDataRsp>>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );

    return {
      data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
        metricName: item.metricName,
        dataPoints: item.dataPoints,
      })),
      timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
      timeType,
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      data: [],
      timePoints: [],
      timeType,
    };
  }
};

export const getMultiDashboard = async (
  metricType: MetricType,
  metricSubTypes: MetricSubType[],
  token: string,
  stats?: Stats[],
): Promise<Dashboard> => {
  try {
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes: metricSubTypes,
      extendOption: {
        multiStats: stats,
      },
      token,
    };
    const rsp = await TRPCPost<NoUndefinedField<IGetDashboardDataRsp>>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );

    return {
      data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
        metricName: item.metricName,
        dataPoints: item.dataPoints,
      })),
      timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      data: [],
      timePoints: [],
    };
  }
};


/**
 * 拉取 dashboard 接口
 * @param metricType
 * @param metricSubTypes
 * @param token
 * @param stats
 */
export const newLoadDashboard = async (
  metricType: MetricType,
  metricSubTypes: MetricSubType[],
  token: string,
  stats?: Stats,
): Promise<Dashboard> => {
  try {
    const reqBody: IGetDashboardDataReq = {
      productId: getProductId(),
      type: metricType,
      subTypes: metricSubTypes,
      extendOption: {
        stats,
      },
      token,
    };
    const rsp = await TRPCPost<NoUndefinedField<IGetDashboardDataRsp>>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.metrics_server.GetMetrics/GetDashboardData`,
      reqBody,
    );

    return {
      data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
        metricName: item.metricName,
        dataPoints: item.dataPoints,
      })),
      timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
    };
  } catch (e) {
    logger.error('loadDashboard err: ', e);
    return {
      data: [],
      timePoints: [],
    };
  }
};

export function filterOption(fields: Record<string, any>) {
  return Object.keys(fields)
    .filter(key => toLineString(key) !== FieldKey.CustomDate)
    .filter(key => key !== FieldKey.TimeRange
      && key !== FieldKey.LaunchTagFilter
      && key !== FieldKey.AppVersionOperator
      && key !== FieldKey.TimeWindow
      && key !== FieldKey.TimeQuick
      && key !== FieldKey.TimeType
      && key !== FieldKey.AccurateDate
      && key !== FieldKey.MinAppVersion
      && key !== FieldKey.MaxAppVersion
      && key !== FieldKey.WildcardAppVersion)
    .filter((key) => {
      if (typeof fields[key] === 'string') {
        return fields[key] !== FIELD_ALL;
      }
      if (Array.isArray(fields[key])) {
        return fields[key]?.length > 0 && !fields?.[key]?.includes?.(FIELD_ALL);
      }
      if (key === FieldKey.AppVersion || FieldKey.OsVersion) {
        return fields?.[key]?.values?.length > 0 && !fields[key]?.values?.includes?.(FIELD_ALL);
      }
      return !!fields[key];
    })
    .map((key) => {
      switch (key) {
        case toLineString(FieldKey.StandardShiplyTag):
          return {
            name: FieldKey.ShiplyAbTestTag,
            key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
            values: toArray(fields?.[toLineString(FieldKey.StandardShiplyTag)]?.values ?? ''),
            operator: fields?.[toLineString(FieldKey.StandardShiplyTag)]?.mode,
            type: KeyDataType.ARRAY,
          };
        case toLineString(FieldKey.StandardAbTestTag):
          return {
            name: FieldKey.ShiplyAbTestTag,
            key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
            values: toArray(fields?.[toLineString(FieldKey.StandardAbTestTag)]?.values ?? ''),
            operator: fields?.[toLineString(FieldKey.StandardAbTestTag)]?.mode,
            type: KeyDataType.ARRAY,
          };
        case FieldKey.LaunchTag:
          return {
            name: key,
            values: toArray(fields[key]),
            operator: fields[FieldKey.LaunchTagFilter],
          };
        case FieldKey.AppVersion:
        case FieldKey.OsVersion:
          return {
            name: key,
            values: toArray(fields[key].values),
            operator: fields[key].operator,
          };
        case FieldKey.OOMType:
          return {
            name: key,
            values: fields[key],
            operator: FieldsOperator.OR,
          };
        default:
          return {
            name: key,
            values: toArray(fields[key]),
          };
      }
    })
    .filter(item => !isEmpty(item.values));
}
